import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Section from "../Section";
import { pageTitle } from "../../helpers/PageTitle";
import BannerSectionStyle3 from "../Section/BannerSection/BannerSectionStyle3";
import WorkingProcess from "../Section/WorkingProcess";
import { FaUserMd, FaHeart, FaHospital, FaClinicMedical, FaStethoscope } from "react-icons/fa";
import styled from "styled-components";
import FaqSectionStyle4 from "../FaqSection/FaqSectionStyle4";
import Spacing from "../Spacing";
import Post from "../Post";
import { Helmet } from "react-helmet";

const faqData = [
  {
    title: "What is embryo donation and who can benefit from it ?",
    content:
      "Embryo donation is a fertility treatment where embryos created from donated eggs and sperm are transferred to a recipient's uterus. This option can benefit couples with infertility issues where both egg and sperm are not viable, and same-sex couples or single individuals who need both egg and sperm donors to achieve pregnancy.",
  },
  {
    title: "What is the process of embryo donation ?",
    content:
      "The process of embryo donation involves several steps: Donor Screening, which includes comprehensive medical, genetic, and psychological screening of donors; Embryo Creation, where donor eggs are fertilized with donor sperm in a laboratory to create embryos; Embryo Freezing and Storage, where healthy embryos are frozen and stored until needed; Recipient Preparation, where the recipient undergoes hormonal treatment to prepare the uterine lining for embryo transfer; Embryo Transfer, where thawed embryos are transferred into the recipient’s uterus; and a Pregnancy Test, where a blood test is performed about two weeks after the transfer to confirm pregnancy.",
  },
  {
    title:
      "What are the success rates of embryo donation at Pluro Jabalpur Fertility Center ?",
    content:
      "The success rates of embryo donation depend on various factors such as the quality of the donated embryos and the age and health of the recipient. On average, the success rates for embryo donation range from 60% to 80% per cycle. Multiple cycles may increase the chances of achieving a successful pregnancy.",
  },
  {
    title:
      "What are the legal and ethical considerations involved in embryo donation ?",
    content:
      "Embryo donation involves several legal and ethical considerations: Informed Consent, where both donors and recipients must provide informed consent, understanding the implications of embryo donation; Confidentiality, which is strictly maintained to protect the privacy of donors and recipients; Legal Rights, where donors typically waive parental rights and recipients are recognized as the legal parents of any resulting children; and Ethical Considerations, ensuring that the process is conducted with respect for all parties involved, including the potential child.",
  },
  {
    title: "Are there any risks associated with embryo donation ?",
    content:
      "Embryo donation is generally a safe procedure, but there are potential risks and considerations, including medical risks for the recipient similar to those of traditional IVF, If hormonal medications are used, and complications from embryo transfer; emotional impact, as the process can be emotionally challenging for both donors and recipients, and counseling is recommended; pregnancy risks, such as miscarriage, ectopic pregnancy, and multiple pregnancies if more than one embryo is transferred; and ethical concerns about the genetic origins of the child and potential future contact between donors and recipients.",
  },
];

const workingProcessData = [
  {
    title: "Initial Consultation and Assessment",
    subTitle:
      "A comprehensive evaluation of the recipient couple is conducted, including a review of medical history, a physical examination, and fertility testing. This is followed by a detailed discussion of the embryo donation process, covering potential risks, success rates, and expected outcomes.",
    iconUrl: "/images/home_2/department_icon1.png",
    number: "01",
  },
  {
    title: "Donor Embryo Matching",
    subTitle:
      "Donor embryos are selected based on compatibility and medical suitability, followed by screening for infectious diseases and genetic conditions.",
    iconUrl: "/images/home_2/departmentLap4.png",
    number: "02",
  },
  {
    title: "Endometrial Preparation",
    subTitle:
      "Medications are administered to the recipient to prepare the uterine lining for embryo implantation, and regular monitoring is performed through blood tests and ultrasounds to ensure optimal endometrial thickness and receptivity.",
    iconUrl: "/images/home_2/department_icon23.png",
    number: "03",
  },
  {
    title: "Embryo Transfer",
    subTitle:
      "Frozen donor embryos are thawed in the laboratory, and one or more healthy embryos are transferred into the recipient’s uterus using a thin, flexible catheter. The procedure is usually painless and does not require anesthesia.",
    iconUrl: "/images/home_2/department_icon3.png",
    number: "04",
  },
  {
    title: "Luteal Phase Support and Follow-Up",
    subTitle:
      "Progesterone or other medications are administered to support the uterine lining and promote implantation, with close monitoring and follow-up appointments scheduled to check for signs of pregnancy and provide ongoing support.",
    iconUrl: "/images/home_2/department_icon24.png",
    number: "05",
  },
  {
    title: "Pregnancy test",
    subTitle:
      "Two weeks after embryo transfer, a pregnancy test confirms if implantation has occurred. Medications like progesterone are used to support implantation, with follow-up appointments scheduled to monitor early pregnancy signs.",
    iconUrl: "/images/home_2/department_icon9.png",
    number: "06",
  },
];

const relatedBlog = [
  {
    title:
      "The Importance of Mental Health: Understanding and Managing Anxiety Disorders",
    thumbUrl: "/images/blog/post_1.jpeg",
    date: "March 12",
    btnText: "Learn More",
    href: "/blog/blog-details",
  },
];

export default function EmbryoDonation() {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('cs_active');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.cs_reveal').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>Embryo Donation Services – Complete Fertility Solution</title>
        <meta name="description" content="Embryo donation for couples seeking a complete fertility solution when other treatment options have not worked." />
        <meta name="keywords" content="embryo donation, fertility solution, donor embryos, IVF with embryo transfer" />
        <link rel="canonical" href="https://jabalpurivfcentre.com/Embryo-Donation" />
      </Helmet>
      <Container>
        <BannerSectionStyle3
          bgUrl="/images/Embryo-Donation.jpeg"
          title="Welcome to <br /> Pluro Jabalpur<br /> Fertility Center"
          subTitle="Embryo Donation"
        />

        <Section topMd={ 200 } topLg={ 150 } topXl={ 110 }>
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                {/* 1. Intro Section */ }
                <div className="cs_iui_intro cs_reveal">
                  <div className="row align-items-center">
                    <div className="col-lg-6">
                      <h1 className="cs_main_heading">Advanced Embryo Donation Programs at Pluro Jabalpur Fertility Centre</h1>
                      <p>
                        Embryo donation is an innovative and compassionate option
                        for couples struggling with infertility. At Pluro Jabalpur
                        Fertility Centre, known as the Best Fertility Clinic in
                        Jabalpur, we offer comprehensive embryo donation programs
                        under the expert care of Dr. Archana Shrivastava, the Best
                        IVF Doctor in Jabalpur. Our goal is to provide the best
                        possible solutions to help you achieve your dream of
                        parenthood through personalized, compassionate care.
                      </p>
                    </div>
                    <div className="col-lg-6">
                      <div className="cs_iui_highlight_box">
                        <h2>What is Embryo Donation?</h2>
                        <p>
                          Embryo donation involves fertilization of donor eggs with
                          donor sperms to create healthy donor embryos. These embryos
                          are donated to recipients who are unable to conceive using
                          their own eggs and sperm. This option offers a unique
                          opportunity for couples to experience pregnancy and
                          childbirth when other fertility treatments have not been
                          successful.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. When is Embryo Donation Recommended */ }
                <div className="cs_iui_recommended cs_reveal">
                  <h2 className="text-center cs_fs_40 cs_medium mb-3">When is Embryo Donation Recommended ?</h2>
                  <p className="text-center mb-5">
                    Embryo donation may be recommended for couples facing the following fertility challenges :
                  </p>

                  <div className="cs_iui_grid">
                    <div className="cs_recommended_card">
                      <div className="cs_rc_icon">01</div>
                      <div className="cs_rc_title">Advanced Maternal Age</div>
                      <p className="cs_rc_desc">Women of advanced reproductive age whose eggs may not be viable for conception.</p>
                    </div>

                    <div className="cs_recommended_card">
                      <div className="cs_rc_icon">02</div>
                      <div className="cs_rc_title">Premature Ovarian Failure</div>
                      <p className="cs_rc_desc">Early menopause or loss of normal ovarian function before the age of 40.</p>
                    </div>

                    <div className="cs_recommended_card">
                      <div className="cs_rc_icon">03</div>
                      <div className="cs_rc_title">Severe Male Factor Infertility</div>
                      <p className="cs_rc_desc">When the male partner has severe issues with sperm quality or quantity.</p>
                    </div>

                    <div className="cs_recommended_card">
                      <div className="cs_rc_icon">04</div>
                      <div className="cs_rc_title">Genetic Disorders</div>
                      <p className="cs_rc_desc">Risk of passing on genetic diseases or conditions.</p>
                    </div>

                    <div className="cs_recommended_card">
                      <div className="cs_rc_icon">05</div>
                      <div className="cs_rc_title">Previous Failed IVF Cycles</div>
                      <p className="cs_rc_desc">Multiple failed IVF attempts due to poor embryo quality or other factors.</p>
                    </div>

                    <div className="cs_recommended_card">
                      <div className="cs_rc_icon">06</div>
                      <div className="cs_rc_title">Unexplained Infertility</div>
                      <p className="cs_rc_desc">When no specific cause of infertility is identified after a complete fertility workup.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Start Working Process Section */ }
        <Section
          topMd={ 100 }
          topLg={ 80 }
          topXl={ 60 }
          bottomMd={ 150 }
          bottomLg={ 120 }
          bottomXl={ 100 }
        >
          <WorkingProcess
            sectionTitle="How Embryo Donation Works"
            sectionTitleUp=""
            sectionTitleDown=""
            sectionSubTitle=""
            data={ workingProcessData }
          />
        </Section>
        {/* End Working Process Section */ }


        {/* Benefits Section */ }
        <Section bottomMd={ 100 } bottomLg={ 80 } bottomXl={ 60 }>
          <div className="container cs_reveal">
            <div className="cs_ivf_factors_section_premium">
              <div className="row align-items-center cs_relative_z1">
                <div className="col-lg-5">
                  <div className="cs_factors_left_premium">
                    <div className="cs_section_badge_premium">Benefits</div>
                    <h2 className="cs_section_title_premium">Benefits of Embryo Donation</h2>
                    <div className="cs_factors_img_wrap">
                      <img src="/images/home_2/NewBornBaby.png" alt="Embryo Donation Benefits" className="cs_factors_img" />
                    </div>
                  </div>
                </div>
                <div className="col-lg-7">
                  <div className="cs_factors_grid_premium">
                    <div className="cs_factor_card_premium">
                      <div className="cs_factor_num_premium">01</div>
                      <div className="cs_factor_content_premium">
                        <h3>High Success Rates</h3>
                        <p>Donor embryos often come from young, healthy individuals, increasing the likelihood of successful implantation and pregnancy.</p>
                      </div>
                    </div>
                    <div className="cs_factor_card_premium">
                      <div className="cs_factor_num_premium">02</div>
                      <div className="cs_factor_content_premium">
                        <h3>Genetic Screening</h3>
                        <p>Donor embryos undergo thorough genetic screening to minimize the risk of hereditary diseases.</p>
                      </div>
                    </div>
                    <div className="cs_factor_card_premium">
                      <div className="cs_factor_num_premium">03</div>
                      <div className="cs_factor_content_premium">
                        <h3>Enabling Parenthood</h3>
                        <p>Provides an opportunity for individuals and couples with severe infertility issues to experience the joy of pregnancy and childbirth.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Why Choose Us Section */ }
        <Section bottomMd={ 120 } bottomLg={ 100 } bottomXl={ 80 }>
          <div className="container cs_reveal">
            <div className="cs_premium_why_choose">
              <div className="cs_premium_section_header">
                <div className="cs_premium_badge">Why Choose Us</div>
                <h2>Why Choose Pluro Jabalpur Fertility Centre for Embryo Donation?</h2>
              </div>

              <div className="row cs_gap_y_40 justify-content-center" style={ { display: 'flex', flexWrap: 'wrap' } }>
                <div className="col-lg-4 col-md-6">
                  <div className="cs_why_card_premium">
                    <div className="cs_wcp_icon">
                      <FaUserMd />
                    </div>
                    <h3>Expertise and Experience</h3>
                    <p>Led by Dr. Archana Shrivastava, our team has extensive experience in managing successful embryo donation programs.</p>
                  </div>
                </div>
                <div className="col-lg-4 col-md-6">
                  <div className="cs_why_card_premium">
                    <div className="cs_wcp_icon">
                      <FaHeart />
                    </div>
                    <h3>Personalized Care</h3>
                    <p>We tailor our donor programs to meet the unique needs of each couple or individual, ensuring the best possible outcomes.</p>
                  </div>
                </div>
                <div className="col-lg-4 col-md-6">
                  <div className="cs_why_card_premium">
                    <div className="cs_wcp_icon">
                      <FaClinicMedical />
                    </div>
                    <h3>Comprehensive Fertility Services</h3>
                    <p>From initial consultation and complete fertility workup to advanced treatments like IVF, ICSI, and donor programs, we offer a full spectrum of fertility services.</p>
                  </div>
                </div>
                <div className="col-lg-6 col-md-6">
                  <div className="cs_why_card_premium">
                    <div className="cs_wcp_icon">
                      <FaHospital />
                    </div>
                    <h3>State-of-the-Art Facility</h3>
                    <p>Our clinic is equipped with the latest technology and facilities to provide top-notch fertility care.</p>
                  </div>
                </div>
                <div className="col-lg-6 col-md-6">
                  <div className="cs_why_card_premium">
                    <div className="cs_wcp_icon">
                      <FaStethoscope />
                    </div>
                    <h3>Confidential and Compassionate</h3>
                    <p>We prioritize your comfort, privacy, and well-being, offering compassionate support throughout your fertility journey.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* FAQ Section */ }
        <Section topMd={ 80 } topLg={ 60 } topXl={ 40 }>
          <div className="cs_iui_faq_wrapper cs_reveal">
            <FaqSectionStyle4
            sectionTitle="Frequently Asked <br />Questions"
            data={ faqData }
            faqImgUrl="/images/home_4/faq_img.jpg"
            spiningImgUrl="/images/home_1/about_mini.svg"
          />
          </div>
        </Section>

        {/* Contact Us Section */ }
        <Section topMd={ 60 } topLg={ 40 } topXl={ 20 } bottomMd={ 100 } bottomLg={ 80 } bottomXl={ 60 }>
          <div className="container cs_reveal">
            <div className="cs_ivf_contact_banner" style={ { background: '#c77842', borderRadius: '20px', padding: '60px 50px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '30px' } }>
              <div className="cs_icc_content" style={ { flex: '1', minWidth: '300px' } }>
                <h2 style={ { color: '#fff', fontSize: '36px', marginBottom: '15px', fontWeight: '700' } }>Contact Us</h2>
                <p style={ { color: 'rgba(255,255,255,0.9)', fontSize: '18px', margin: '0', lineHeight: '1.6' } }>
                  If you are considering embryo donation as part of your
                  fertility treatment, contact Pluro Jabalpur Fertility Centre
                  today. Our expert team is here to guide you through the
                  process and help you achieve your dream of starting a
                  family.
                </p>
              </div>
              <div className="cs_icc_action">
                <Link to="/contact" className="cs_btn" style={ { background: '#fff', color: '#c77842', padding: '15px 40px', borderRadius: '50px', fontWeight: '600', fontSize: '18px', display: 'inline-flex', alignItems: 'center', textDecoration: 'none', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' } }>
                  <span>Get in Touch</span>
                </Link>
              </div>
            </div>
          </div>
        </Section>

        <Section bottomMd={ 200 } bottomLg={ 150 } bottomXl={ 110 }>
          <div className="container cs_reveal">
            <h2 className="mb-0 cs_fs_40 cs_medium">Related Articles</h2>
            <Spacing md="57" />
            <div className="row cs_gap_y_40">
              { relatedBlog?.map((item, index) => (
                <div className="col-xl-12" key={ index }>
                  <Post { ...item } />
                </div>
              )) }
            </div>
          </div>
        </Section>
      </Container>
    </>
  );
}

const Container = styled.div`
  .margin {
    margin-left: 25px;
  }
  .heading{
    font-size: 32px;
     @media (max-width: 991px) {
      font-size: 28px;
    }
  }
`;
